import { Paper } from '@mui/material';
import {
  AssortmentItemsActionToolbar,
  CentralAssortmentTable,
  useAssortmentItemActions,
} from '@retail/my-assortment/components';
import { AssortmentItemMinimal } from '@retail/my-assortment/types';
import {
  BulkEditItemsDialogContainer,
  EditAssortmentTagsDialogContainer,
  MgItemContainer,
  MgItemDialogContainer,
} from '@retail/products/containers';
import { useSelectedSearchParamState } from '@shared/hooks';
import { DeleteItemsDialogContainer } from '../../AssortmentItems';
import { ExternalItemSource } from '@retail/products/components';
import { useDeleteSupplierItemAssortmentTag } from '@retail/products/data-access';
import { useCallback, useState } from 'react';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@retail/hooks';
import { DeleteDialog } from '@retail/components';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { useCentralAssortmentItems } from './useCentralAssortmentItems';

interface Props {
  assortmentId: number;
  externalItemSource: ExternalItemSource;
  toggleAddItemsDialog: () => void;
}

export function CentralAssortmentItemsContainer({
  assortmentId,
  externalItemSource,
  toggleAddItemsDialog,
}: Props) {
  const { filteredItems: assortmentItems, isFetching } = useCentralAssortmentItems({
    suspense: false,
    useErrorBoundary: true,
  });

  const t = useMyAssortmentTFunction();
  const queryClient = useQueryClient();
  const toast = useToast();
  const {
    itemsChecked,
    bulkEditPriorities,
    bulkEditTags,
    itemsForDeletion,
    setItemForDeletion,
    resetDeletedItems,
    checkItems,
    resetCheckedItems,
    toggleBulkEditPriorities,
    toggleBulkEditTags,
  } = useAssortmentItemActions((state) => state);

  const { userCanRead } = usePermissionsForCurrentUser();
  const { appTenant } = useSelectedOrgUnit();
  const isPriceCalculationEnabled = userCanRead('PRICE_CALCULATION');

  const [selectedItem, setSelectedItem, selectItemById] = useSelectedSearchParamState({
    options: assortmentItems,
    searchParamKey: 'item',
    getOptionId: ({ mgSupplierItemId }) => mgSupplierItemId,
  });

  const [supplierItemAssortmentTagToDelete, setSupplierItemAssortmentTagToDelete] = useState<
    { tag: SupplierItemAssortmentTag; mgSupplierItemId: number } | undefined
  >(undefined);

  const { mutateAsync: deleteSupplierItemFromAssortmentTag } = useDeleteSupplierItemAssortmentTag();
  const handleDeleteAssortmentItemTag = useCallback(
    (tag: SupplierItemAssortmentTag, supplierItemId: number) =>
      deleteSupplierItemFromAssortmentTag({
        urlVariables: { tagId: tag.tagId, assortmentId, supplierItemId },
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['central-assortments'] });
          toast.success(
            t('myAssortment.actions.deletedItem', {
              assortmentName: tag.tagName,
            })
          );
        })
        .catch((err: Error) => {
          console.error(err);
          toast.error(t('myAssortment.actions.genericActionError'));
        }),
    [assortmentId, deleteSupplierItemFromAssortmentTag, queryClient, t, toast]
  );

  return (
    <Paper sx={{ display: 'flex', flexGrow: 1, overflow: 'auto' }}>
      <CentralAssortmentTable
        items={assortmentItems}
        itemsChecked={itemsChecked}
        checkboxSelection
        loading={isFetching}
        setItemsChecked={checkItems}
        viewItem={selectItemById}
        externalItemSource={externalItemSource}
        showMainSupplierName={appTenant.hasMainSupplier}
        toggleAddItemsDialog={toggleAddItemsDialog}
        deleteAssortmentItemTag={(tag, mgSupplierItemId) =>
          setSupplierItemAssortmentTagToDelete({ tag, mgSupplierItemId })
        }
        deleteItem={setItemForDeletion}
      />

      {selectedItem && (
        <MgItemDialogContainer
          open
          onClose={() => setSelectedItem(null)}
          itemNumber={selectedItem.mgItemNumber}
        >
          {({ mgItem }) => (
            <MgItemContainer mgItem={mgItem} showAddItem={false} hiddenBreadcrumbs />
          )}
        </MgItemDialogContainer>
      )}
      {!!itemsForDeletion.length && (
        <DeleteItemsDialogContainer
          assortmentId={assortmentId}
          itemsForDeletion={itemsForDeletion}
          resetCheckedItems={resetDeletedItems}
        />
      )}
      {bulkEditPriorities && (
        <BulkEditItemsDialogContainer
          selectedItems={
            assortmentItems
              .filter((item) => itemsChecked.includes(item.mgSupplierItemId))
              .map((item) => {
                return { ...item, prioritised: item.priority.prioritised };
              }) as AssortmentItemMinimal[]
          }
          onClose={() => toggleBulkEditPriorities(false)}
          resetCheckedItems={resetCheckedItems}
        />
      )}
      {bulkEditTags && (
        <EditAssortmentTagsDialogContainer
          assortmentId={assortmentId}
          selectedItems={
            assortmentItems.filter((item) =>
              itemsChecked.includes(item.mgSupplierItemId)
            ) as AssortmentItemMinimal[]
          }
          onClose={() => toggleBulkEditTags(false)}
          resetCheckedItems={resetCheckedItems}
        />
      )}
      {supplierItemAssortmentTagToDelete && (
        <DeleteDialog
          isOpen
          dialogTitle={t('myAssortment.centralAssortments.deleteSupplierItemAssortmentTag')}
          message={t('myAssortment.centralAssortments.confirmDelete', {
            assortmentTag: supplierItemAssortmentTagToDelete.tag.tagName,
          })}
          onDelete={() =>
            handleDeleteAssortmentItemTag(
              supplierItemAssortmentTagToDelete.tag,
              supplierItemAssortmentTagToDelete.mgSupplierItemId
            )
          }
          onClose={() => setSupplierItemAssortmentTagToDelete(undefined)}
        />
      )}

      {!!itemsChecked.length && (
        <AssortmentItemsActionToolbar
          totalItems={assortmentItems.length}
          isUpdatePricesEnabled={isPriceCalculationEnabled}
        />
      )}
    </Paper>
  );
}
